"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Wizard = void 0;
const react_1 = __importStar(require("react"));
const rxjs_1 = require("rxjs");
const core_1 = require("@veeam/core");
const Dialog_1 = require("../Dialog");
const helpers_1 = require("../helpers");
const TabList_1 = require("../TabList");
const View_1 = require("../View");
const createEnableStepsSchema_1 = require("./helpers/createEnableStepsSchema");
const WizardSize_1 = require("./types/WizardSize");
const Wizard_styles_1 = require("./Wizard.styles");
const WizardHeader_1 = require("./components/WizardHeader/WizardHeader");
exports.Wizard = (0, helpers_1.createUIElement)('Wizard', (_a, ref) => {
    var { className, id, loading, onRequestClose, header, width = WizardSize_1.WIZARD_SIZE.m, height = WizardSize_1.WIZARD_SIZE.m, tabWidth, steps, onStepChange, initialStepIdx = 0, onDone, canSave, buttons, hasTopPadding = true, hasKeyboardInteractive = true, ['data-selector-id']: initialDataSelectorId, onKeyPress, style } = _a, rest = __rest(_a, ["className", "id", "loading", "onRequestClose", "header", "width", "height", "tabWidth", "steps", "onStepChange", "initialStepIdx", "onDone", "canSave", "buttons", "hasTopPadding", "hasKeyboardInteractive", 'data-selector-id', "onKeyPress", "style"]);
    const dataSelectorId = (0, react_1.useMemo)(() => initialDataSelectorId || 'wizard', [initialDataSelectorId]);
    const { t } = (0, core_1.useFeatureContext)();
    const btns = (0, react_1.useMemo)(() => buttons || {}, [buttons]);
    btns.prev = (0, react_1.useMemo)(() => btns.prev || t('wizard|actions|previous') || 'Previous', [btns.prev, t]);
    btns.next = (0, react_1.useMemo)(() => btns.next || t('wizard|actions|next') || 'Next', [btns.next, t]);
    btns.done = (0, react_1.useMemo)(() => btns.done || t('wizard|actions|done') || 'Finish', [btns.done, t]);
    btns.cancel = (0, react_1.useMemo)(() => btns.cancel || t('wizard|actions|cancel') || 'Cancel', [btns.cancel, t]);
    const calculatedInitialStepIdx = (0, react_1.useMemo)(() => {
        for (let idx = initialStepIdx; idx < steps.length; idx++) {
            if (!steps[idx].hidden && !steps[idx].disabled) {
                return idx;
            }
        }
        return initialStepIdx;
    }, [initialStepIdx, steps]);
    const [currentStepIdx, setStep] = (0, react_1.useState)(calculatedInitialStepIdx);
    (0, react_1.useEffect)(() => {
        setStep(calculatedInitialStepIdx);
    }, [calculatedInitialStepIdx]);
    const currentStep = (0, react_1.useMemo)(() => steps[currentStepIdx], [currentStepIdx, steps]);
    const [enableStepsSchema, setEnableStepsSchema] = (0, react_1.useState)((0, createEnableStepsSchema_1.createEnableStepsSchema)(steps));
    (0, react_1.useEffect)(() => {
        const stepFormObservers = steps
            .map((step) => step.form && step.form.asObserver())
            .filter(Boolean);
        const subscription = (0, rxjs_1.combineLatest)(stepFormObservers).subscribe(() => {
            setEnableStepsSchema((0, createEnableStepsSchema_1.createEnableStepsSchema)(steps));
        });
        return () => subscription.unsubscribe();
    }, [steps]);
    const hasPrevStep = (0, react_1.useCallback)(() => {
        let idx = currentStepIdx - 1;
        for (; idx >= 0; idx--) {
            if (!steps[idx].disabled && !steps[idx].hidden) {
                break;
            }
        }
        return !!steps[idx] && !steps[idx].disabled;
    }, [currentStepIdx, steps]);
    const nextStep = (0, react_1.useMemo)(() => steps[currentStepIdx + 1], [currentStepIdx, steps]);
    const validateStep = (0, react_1.useCallback)((idx) => __awaiter(void 0, void 0, void 0, function* () {
        let isValid = true;
        const step = steps[idx];
        if (step.hidden || step.disabled) {
            return true;
        }
        if (step.form && step.form.submit) {
            yield step.form.submit();
            isValid = step.form.isValid();
        }
        if (isValid && step.validate) {
            isValid = isValid && (yield step.validate());
        }
        return isValid;
    }), [steps]);
    const validateSteps = (0, react_1.useCallback)((startIdx, endIdx) => __awaiter(void 0, void 0, void 0, function* () {
        let isValid = true;
        for (let i = startIdx; i <= endIdx; i++) {
            isValid = yield validateStep(i);
            if (!isValid) {
                setStep(i);
                break;
            }
        }
        return isValid;
    }), [validateStep]);
    const selectHandler = (0, react_1.useCallback)((selectIdx, direction = 0) => __awaiter(void 0, void 0, void 0, function* () {
        let canSelect = true;
        if (steps[selectIdx].disabled) {
            // next button or prev pressed, but needed step disabled
            let idx = selectIdx + direction;
            for (; idx < steps.length && idx >= 0; idx += direction) {
                if (!steps[idx].disabled) {
                    selectIdx = idx;
                    break;
                }
            }
            if (idx === steps.length || idx < 0) {
                return;
            }
        }
        if (selectIdx > currentStepIdx) {
            canSelect = yield validateSteps(currentStepIdx, selectIdx - 1);
        }
        if (!canSelect) {
            return;
        }
        setStep(selectIdx);
        if (onStepChange) {
            onStepChange(steps[selectIdx].tag, steps[currentStepIdx].tag);
        }
    }), [currentStepIdx, onStepChange, steps, validateSteps]);
    const nextHandler = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        const nextIdx = currentStepIdx + 1;
        yield selectHandler(nextIdx, 1);
    }), [currentStepIdx, selectHandler]);
    const prevHandler = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        const prevIdx = currentStepIdx - 1;
        yield selectHandler(prevIdx, -1);
    }), [currentStepIdx, selectHandler]);
    const doneHandler = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        let isValid = true;
        if (canSave) {
            if (currentStepIdx < steps.length - 1) {
                isValid = yield validateSteps(currentStepIdx, steps.length - 1); // last (summary) step should be validated, too
            }
        }
        if (isValid && onDone) {
            onDone();
        }
    }), [canSave, currentStepIdx, onDone, steps.length, validateSteps]);
    const enterKeyHandler = (0, react_1.useCallback)(() => {
        if (!hasKeyboardInteractive || loading) {
            return;
        }
        if (nextStep) {
            nextHandler().catch((e) => console.error(`Next handler error: ${e}`));
            return;
        }
        doneHandler().catch((e) => console.error(`Done handler error: ${e}`));
    }, [doneHandler, hasKeyboardInteractive, loading, nextHandler, nextStep]);
    const items = [];
    steps.forEach(({ hidden, tag, disabled, tabTitle }, idx) => {
        if (!hidden) {
            items.push({
                id: `tab-${idx}`,
                active: idx === currentStepIdx,
                disabled: disabled || !enableStepsSchema[tag],
                onClick: () => {
                    if (!disabled && enableStepsSchema[tag]) {
                        selectHandler(idx).catch((e) => console.error(`Selected handler error: ${e}`));
                    }
                },
                title: tabTitle
            });
        }
    });
    const wizardActions = (0, react_1.useMemo)(() => [
        hasPrevStep() && {
            id: 'previous',
            text: btns.prev,
            view: Dialog_1.ACTION_VIEW.alternate,
            onClick: prevHandler
        },
        !!nextStep && {
            id: 'next',
            text: btns.next,
            view: Dialog_1.ACTION_VIEW.primary,
            onClick: nextHandler
        },
        (!nextStep || canSave) && {
            id: 'done',
            text: btns.done,
            view: Dialog_1.ACTION_VIEW.primary,
            onClick: doneHandler
        },
        {
            id: 'cancel',
            text: btns.cancel,
            view: 'secondary',
            onClick: onRequestClose
        }
    ].filter(Boolean), [
        btns.cancel,
        btns.done,
        btns.next,
        btns.prev,
        canSave,
        doneHandler,
        hasPrevStep,
        nextHandler,
        nextStep,
        onRequestClose,
        prevHandler
    ]);
    const wizardSize = (0, react_1.useMemo)(() => ({
        width,
        height
    }), [height, width]);
    const onKeyPressHandler = (0, react_1.useCallback)((e) => {
        if (e.key === 'Enter') {
            enterKeyHandler();
        }
        onKeyPress === null || onKeyPress === void 0 ? void 0 : onKeyPress(e);
    }, [enterKeyHandler, onKeyPress]);
    if (!currentStep) {
        return null;
    }
    return (react_1.default.createElement(Dialog_1.Dialog, Object.assign({}, rest, { ref: ref, style: style, "data-selector-id": dataSelectorId, id: id, hasSpacing: false, loading: loading, header: header, hasTopPadding: hasTopPadding, deactivate: onRequestClose, actions: wizardActions, size: wizardSize, onRequestClose: onRequestClose, onKeyPress: onKeyPressHandler, className: (0, helpers_1.cn)('vm-Wizard', className) }),
        react_1.default.createElement(Wizard_styles_1.StyledWizardTabs, { style: { height: '100%' } },
            react_1.default.createElement(TabList_1.TabList, { "data-selector-id": `${dataSelectorId}-tabs`, width: tabWidth, items: items }),
            !currentStep.hidden && (react_1.default.createElement(Wizard_styles_1.StyledWizardPanel, null,
                react_1.default.createElement(WizardHeader_1.WizardHeader, { title: currentStep.title, description: currentStep.description }),
                react_1.default.createElement(Wizard_styles_1.StyledWizardBody, { hasTopPadding: hasTopPadding, spaceHorizontal: View_1.INDENT.m, spaceBottom: View_1.INDENT.m, root: true, clipped: true, hasYScroll: true, contentMod: View_1.VIEW_CONTENT_MOD.fill }, currentStep.body))))));
});
//# sourceMappingURL=Wizard.js.map